import { dataUrl, normalizePath } from "../util/path";

export const COMPILED_TERRAIN_FORMAT = "rose-runtime-map-v1" as const;
export type TerrainSourceMode = "legacy" | "compiled" | "auto";

export interface CompiledTerrainTile {
    layer1: number;
    layer2: number;
    blend: boolean;
    rotation: number;
    type: number;
}

export interface CompiledTerrainChunk {
    id: string;
    legacyChunkName?: string;
    origin: [number, number, number];
    heightfield: {
        width: number;
        height: number;
        spacing: number;
        values: number[];
    };
    surface: {
        width: number;
        height: number;
        textures: string[];
        tiles: CompiledTerrainTile[];
        cells: number[];
    };
    lightmap?: {
        width: number;
        height: number;
        rgbaBase64: string;
    };
}

export interface CompiledMapTransform {
    position: [number, number, number];
    quaternion: [number, number, number, number];
    scale: [number, number, number];
    visible: boolean;
}

export interface CompiledMapGameplay {
    settings: {
        musicDay: string;
        musicNight: string;
        minimap: string;
        minimapStartX: number;
        minimapStartY: number;
        [key: string]: unknown;
    };
    monsterSpawns: unknown[];
    npcs: unknown[];
    warps: Array<{
        id: number;
        srcX: number;
        srcY: number;
        srcZ: number;
        quaternion: [number, number, number, number];
        scale: [number, number, number];
        destMap: number;
        destX: number;
        destY: number;
    }>;
    spawnPoints: unknown[];
}

export interface CompiledMapVisual {
    staticObjects: Array<CompiledMapTransform & {
        id: string;
        name: string;
        modelListPath: string;
        objectId: number;
        category: "construction" | "decoration";
        lightmap?: {
            object: unknown;
            textures: string[];
            embeddedTextures: Array<null | {
                width: number;
                height: number;
                rgbaBase64: string;
            }>;
        };
    }>;
    waterPlanes: Array<CompiledMapTransform & {
        start: [number, number, number];
        end: [number, number, number];
    }>;
    waterPatches: Array<CompiledMapTransform & Record<string, unknown>>;
    effects: Array<CompiledMapTransform & { filePath: string }>;
    animations: Array<CompiledMapTransform & { objectId: number }>;
    ambientSounds: Array<CompiledMapTransform & {
        filePath: string;
        range: number;
        interval: number;
    }>;
    collisions: Array<CompiledMapTransform & Record<string, unknown>>;
    events: Array<CompiledMapTransform & Record<string, unknown>>;
    sky: null | {
        meshPath: string;
        dayTexture: string;
        nightTexture: string;
        blendRatio: number;
    };
    music: null | {
        audioPath: string;
        nightAudioPath: string;
        volume: number;
        loop: boolean;
    };
    weather: unknown[];
}

export interface CompiledTerrainPackage {
    format: typeof COMPILED_TERRAIN_FORMAT;
    sourceFormat: "rose-terrain-v1";
    mapId: number;
    projectId: string;
    revision: string;
    chunks: CompiledTerrainChunk[];
    gameplay: CompiledMapGameplay;
    visual: CompiledMapVisual;
}

export interface RuntimeTerrainData {
    heightmap: {
        width: number;
        height: number;
        gridCount: number;
        patchSize: number;
        map: number[];
    };
    tilemap: {
        width: number;
        height: number;
        map: Array<{ brush: number; index: number; set: number; number: number }>;
    };
    textures: string[];
    tiles: CompiledTerrainTile[];
}

function positiveInteger(value: unknown, label: string): asserts value is number {
    if (!Number.isInteger(value) || Number(value) <= 0) {
        throw new Error(`${label} must be a positive integer`);
    }
}

function finiteTuple3(value: unknown, label: string): asserts value is [number, number, number] {
    if (
        !Array.isArray(value) || value.length !== 3 ||
        value.some((entry) => !Number.isFinite(entry))
    ) {
        throw new Error(`${label} must contain three finite numbers`);
    }
}

function finiteTuple4(
    value: unknown,
    label: string,
): asserts value is [number, number, number, number] {
    if (
        !Array.isArray(value) || value.length !== 4 ||
        value.some((entry) => !Number.isFinite(entry))
    ) {
        throw new Error(`${label} must contain four finite numbers`);
    }
}

function validateTransform(value: any, label: string): void {
    finiteTuple3(value?.position, `${label} position`);
    finiteTuple4(value?.quaternion, `${label} quaternion`);
    finiteTuple3(value?.scale, `${label} scale`);
    if (typeof value.visible !== "boolean") {
        throw new Error(`${label} visibility must be boolean`);
    }
}

export function validateCompiledTerrainPackage(
    value: unknown,
    expectedMapId?: number,
): CompiledTerrainPackage {
    const pkg = value as CompiledTerrainPackage;
    if (!pkg || pkg.format !== COMPILED_TERRAIN_FORMAT) {
        throw new Error(`Unsupported compiled terrain format: ${pkg?.format ?? "missing"}`);
    }
    if (pkg.sourceFormat !== "rose-terrain-v1") {
        throw new Error(`Unsupported terrain source format: ${pkg.sourceFormat ?? "missing"}`);
    }
    positiveInteger(pkg.mapId, "Compiled terrain map ID");
    if (expectedMapId !== undefined && pkg.mapId !== expectedMapId) {
        throw new Error(
            `Compiled terrain map ID ${pkg.mapId} does not match requested map ${expectedMapId}`,
        );
    }
    if (!pkg.projectId || !pkg.revision) {
        throw new Error("Compiled terrain is missing its project ID or revision");
    }
    if (!Array.isArray(pkg.chunks) || pkg.chunks.length === 0) {
        throw new Error("Compiled terrain contains no chunks");
    }
    if (
        !pkg.gameplay?.settings || !Array.isArray(pkg.gameplay.monsterSpawns) ||
        !Array.isArray(pkg.gameplay.npcs) || !Array.isArray(pkg.gameplay.warps) ||
        !Array.isArray(pkg.gameplay.spawnPoints)
    ) {
        throw new Error("Compiled map has no valid gameplay package");
    }
    const visual = pkg.visual;
    if (
        !visual || !Array.isArray(visual.staticObjects) ||
        !Array.isArray(visual.waterPlanes) || !Array.isArray(visual.waterPatches) ||
        !Array.isArray(visual.effects) || !Array.isArray(visual.animations) ||
        !Array.isArray(visual.ambientSounds) || !Array.isArray(visual.collisions) ||
        !Array.isArray(visual.events) || !Array.isArray(visual.weather)
    ) {
        throw new Error("Compiled map has no valid visual package");
    }
    for (const [category, placements] of Object.entries({
        staticObjects: visual.staticObjects,
        waterPlanes: visual.waterPlanes,
        waterPatches: visual.waterPatches,
        effects: visual.effects,
        animations: visual.animations,
        ambientSounds: visual.ambientSounds,
        collisions: visual.collisions,
        events: visual.events,
    })) {
        placements.forEach((placement, index) =>
            validateTransform(placement, `Compiled ${category} placement ${index}`)
        );
    }
    for (const [index, placement] of visual.staticObjects.entries()) {
        if (!placement.modelListPath || !Number.isInteger(placement.objectId) || placement.objectId < 0) {
            throw new Error(`Compiled static-object placement ${index} has an invalid model`);
        }
        for (const [textureIndex, embedded] of (placement.lightmap?.embeddedTextures ?? []).entries()) {
            if (!embedded) continue;
            positiveInteger(embedded.width, `Static-object lightmap ${index}:${textureIndex} width`);
            positiveInteger(embedded.height, `Static-object lightmap ${index}:${textureIndex} height`);
            let decodedLength = 0;
            try {
                decodedLength = atob(embedded.rgbaBase64).length;
            } catch (_error) {
                throw new Error(`Static-object lightmap ${index}:${textureIndex} is not valid base64`);
            }
            if (decodedLength !== embedded.width * embedded.height * 4) {
                throw new Error(`Static-object lightmap ${index}:${textureIndex} size is invalid`);
            }
        }
    }
    for (const [index, warp] of pkg.gameplay.warps.entries()) {
        if (
            !Number.isInteger(warp.id) || warp.id <= 0 ||
            !Number.isFinite(warp.srcX) || !Number.isFinite(warp.srcY) ||
            !Number.isFinite(warp.srcZ)
        ) {
            throw new Error(`Compiled warp ${index} has an invalid source`);
        }
        finiteTuple4(warp.quaternion, `Compiled warp ${index} quaternion`);
        finiteTuple3(warp.scale, `Compiled warp ${index} scale`);
    }

    const ids = new Set<string>();
    for (const chunk of pkg.chunks) {
        if (!chunk?.id || ids.has(chunk.id)) {
            throw new Error(`Compiled terrain has a missing or duplicate chunk ID: ${chunk?.id}`);
        }
        ids.add(chunk.id);
        finiteTuple3(chunk.origin, `Terrain chunk ${chunk.id} origin`);

        const heightfield = chunk.heightfield;
        positiveInteger(heightfield?.width, `Terrain chunk ${chunk.id} heightfield width`);
        positiveInteger(heightfield?.height, `Terrain chunk ${chunk.id} heightfield height`);
        if (heightfield.width !== 65 || heightfield.height !== 65) {
            throw new Error(`Terrain chunk ${chunk.id} heightfield must be 65x65`);
        }
        if (!Number.isFinite(heightfield.spacing) || heightfield.spacing <= 0) {
            throw new Error(`Terrain chunk ${chunk.id} has invalid height spacing`);
        }
        if (
            !Array.isArray(heightfield.values) ||
            heightfield.values.length !== heightfield.width * heightfield.height ||
            heightfield.values.some((height) => !Number.isFinite(height))
        ) {
            throw new Error(`Terrain chunk ${chunk.id} has an invalid heightfield`);
        }

        const surface = chunk.surface;
        positiveInteger(surface?.width, `Terrain chunk ${chunk.id} surface width`);
        positiveInteger(surface?.height, `Terrain chunk ${chunk.id} surface height`);
        if (surface.width !== 16 || surface.height !== 16) {
            throw new Error(`Terrain chunk ${chunk.id} surface must be 16x16`);
        }
        if (!Array.isArray(surface.textures) || surface.textures.length === 0) {
            throw new Error(`Terrain chunk ${chunk.id} has no textures`);
        }
        if (!Array.isArray(surface.tiles) || surface.tiles.length === 0) {
            throw new Error(`Terrain chunk ${chunk.id} has no tile definitions`);
        }
        for (const tile of surface.tiles) {
            if (
                !Number.isInteger(tile.layer1) || !Number.isInteger(tile.layer2) ||
                tile.layer1 < 0 || tile.layer2 < 0 ||
                tile.layer1 >= surface.textures.length ||
                tile.layer2 >= surface.textures.length
            ) {
                throw new Error(`Terrain chunk ${chunk.id} has an invalid tile texture reference`);
            }
        }
        if (
            !Array.isArray(surface.cells) ||
            surface.cells.length !== surface.width * surface.height ||
            surface.cells.some((tile) =>
                !Number.isInteger(tile) || tile < 0 || tile >= surface.tiles.length
            )
        ) {
            throw new Error(`Terrain chunk ${chunk.id} has invalid surface cells`);
        }
        if (chunk.lightmap) {
            positiveInteger(chunk.lightmap.width, `Terrain chunk ${chunk.id} lightmap width`);
            positiveInteger(chunk.lightmap.height, `Terrain chunk ${chunk.id} lightmap height`);
            if (!chunk.lightmap.rgbaBase64) {
                throw new Error(`Terrain chunk ${chunk.id} lightmap has no pixels`);
            }
            let decodedLength = 0;
            try {
                decodedLength = atob(chunk.lightmap.rgbaBase64).length;
            } catch (_error) {
                throw new Error(`Terrain chunk ${chunk.id} lightmap is not valid base64`);
            }
            if (decodedLength !== chunk.lightmap.width * chunk.lightmap.height * 4) {
                throw new Error(`Terrain chunk ${chunk.id} lightmap size is invalid`);
            }
        }
    }
    return pkg;
}

export function compiledTerrainPath(basePath: unknown, mapId: number): string {
    const base = typeof basePath === "string" && basePath.trim()
        ? basePath.trim()
        : "compiled-maps";
    return `${normalizePath(base).replace(/\/+$/, "")}/${mapId}.map.json`;
}

export async function loadCompiledTerrainPackage(
    mapId: number,
    mode: TerrainSourceMode,
    basePath: unknown,
    fetcher: typeof fetch = fetch,
): Promise<CompiledTerrainPackage | null> {
    if (mode === "legacy") return null;
    const path = compiledTerrainPath(basePath, mapId);
    const response = await fetcher(dataUrl(path));
    if (!response.ok) {
        if (mode === "auto" && response.status === 404) return null;
        throw new Error(
            `Unable to load compiled terrain for map ${mapId} (${response.status} ${response.statusText})`,
        );
    }
    return validateCompiledTerrainPackage(await response.json(), mapId);
}

/** Adapt visual-order project rows to the legacy renderer's inverted rows and centimetre heights. */
export function compiledChunkToRuntimeData(chunk: CompiledTerrainChunk): RuntimeTerrainData {
    const heights = new Array<number>(chunk.heightfield.values.length);
    for (let visualY = 0; visualY < chunk.heightfield.height; visualY++) {
        const rawY = chunk.heightfield.height - 1 - visualY;
        for (let x = 0; x < chunk.heightfield.width; x++) {
            heights[rawY * chunk.heightfield.width + x] =
                chunk.heightfield.values[visualY * chunk.heightfield.width + x] / 0.01;
        }
    }

    const cells = new Array<RuntimeTerrainData["tilemap"]["map"][number]>(
        chunk.surface.cells.length,
    );
    for (let visualY = 0; visualY < chunk.surface.height; visualY++) {
        const rawY = chunk.surface.height - 1 - visualY;
        for (let x = 0; x < chunk.surface.width; x++) {
            cells[rawY * chunk.surface.width + x] = {
                brush: 0,
                index: 0,
                set: 0,
                number: chunk.surface.cells[visualY * chunk.surface.width + x],
            };
        }
    }

    return {
        heightmap: {
            width: chunk.heightfield.width,
            height: chunk.heightfield.height,
            gridCount: Math.max(1, (chunk.heightfield.width - 1) / 16),
            patchSize: chunk.heightfield.spacing,
            map: heights,
        },
        tilemap: {
            width: chunk.surface.width,
            height: chunk.surface.height,
            map: cells,
        },
        textures: [...chunk.surface.textures],
        tiles: chunk.surface.tiles.map((tile) => ({ ...tile })),
    };
}
